<script type="text/javascript">
	import moment from 'moment';
	import Chart from "chart.js";
	import { onMount, afterUpdate, tick, onDestroy } from 'svelte';
	import { get, writable } from 'svelte/store';

	import WeatherStore from './Store';

	import User from '../Branding/User';
	import Feature from '../Branding/Feature';

	export let data;							// = WeatherStore.results.weather;
	export let timeSlider; 						// = WeatherStore.timeSliderData;
	export let propertyIndex;					// propertyIndex (int)
	export let propertyOptions = [];			// [] (GraphProperty)
	export let height;							// height in pixels (numeric)

	let inPropertyIndex = writable(propertyIndex);

	let yAxisLabel = "";

	let doProperties = [];
	let titleDisplayString = "";

	let timeSliderValue = writable(0);
	let timeSliderValueMax = writable(0);

	$: {
		$timeSliderValue;
		$timeSliderValueMax;
		if (data) {
			timeSliderValue = timeSlider.value;
			timeSliderValueMax = timeSlider.max;
		}
	}
	$: {
		$inPropertyIndex;
		console.log("inPropertyIndex", $inPropertyIndex);
		updateChartAxes();
		updateChartData();
	}

	let canvas = null;
	let chart = null;

	let dataPointsBeforeTime = null;
	let dataPoints = null;

	onMount(()=>{
		Chart.defaults.global.defaultFontFamily = "'Open Sans', 'Arial', sans-serif";
		// Chart.defaults.global.animation.duration = 0;
		updateChart();
	});
	onDestroy(()=>{
		if (chart !== null) {
			chart.destroy();
			chart = null;
		}
	});

	let findGraphPropertyByKey = (key) => {
		for (let i = 0; i < propertyOptions.length; i++) {
			const option = propertyOptions[i];
			if (Array.isArray(option)) {
				for (let j = 0; j < option.length; j++) {
					if (option[j].key === key) {
						return option[j];
					}
				}
			}
			else {
				if (option.key === key) {
					return option;
				}
			}
		}
		console.warn("could not find graph property by key", key);
		return null;
	};
	let joinNames = (propertyOptionsArray, includeUnits = false, joinTerm = " & ") => { // Concat the names together by "vs"
		let propertyName = "" ;
		for(let i = 0; i < propertyOptionsArray.length; i++) {
			propertyName += propertyOptionsArray[i].name;
			if (includeUnits) {
				propertyName += " (" + propertyOptionsArray[i].unitType.shortname + ")"
			}
			if (i !== propertyOptionsArray.length - 1) {
				propertyName += joinTerm;
			}
		}
		return propertyName;
	}

	let updateChartAxes = () => {

		let propertyName = "";
		let stepSize = null;
		let suggestedMin = null;
		let suggestedMax = null;
		if (Array.isArray(propertyOptions[$inPropertyIndex])) { // Concat the names together by "vs"
			propertyName = joinNames(propertyOptions[$inPropertyIndex], true);

			if (typeof propertyOptions[$inPropertyIndex][0].ticks.stepSize !== 'undefined') {
				stepSize = propertyOptions[$inPropertyIndex][0].ticks.stepSize;
			}
			if (typeof propertyOptions[$inPropertyIndex][0].ticks.suggestedMin !== 'undefined') {
				suggestedMin = propertyOptions[$inPropertyIndex][0].ticks.suggestedMin;
			}
			if (typeof propertyOptions[$inPropertyIndex][0].ticks.suggestedMax !== 'undefined') {
				suggestedMax = propertyOptions[$inPropertyIndex][0].ticks.suggestedMax;
			}
		}
		else {
			propertyName = propertyOptions[$inPropertyIndex].name + " ("+propertyOptions[$inPropertyIndex].unitType.shortname+")";
			if (typeof propertyOptions[$inPropertyIndex].ticks.stepSize !== 'undefined') {
				stepSize = propertyOptions[$inPropertyIndex].ticks.stepSize;
			}
			if (typeof propertyOptions[$inPropertyIndex].ticks.suggestedMin !== 'undefined') {
				suggestedMin = propertyOptions[$inPropertyIndex].ticks.suggestedMin;
			}
			if (typeof propertyOptions[$inPropertyIndex].ticks.suggestedMax !== 'undefined') {
				suggestedMax = propertyOptions[$inPropertyIndex].ticks.suggestedMax;
			}
		}
		yAxisLabel = propertyName;

		doProperties = [];
		if (Array.isArray(propertyOptions[$inPropertyIndex])) {
			for(let i = 0; i < propertyOptions[$inPropertyIndex].length; i++) {
				doProperties.push(propertyOptions[$inPropertyIndex][i].key)
			}
		}
		else {
			doProperties.push(propertyOptions[$inPropertyIndex].key);
		}

		if (chart !== null) {
			chart.options.scales.yAxes[0].scaleLabel.labelString = yAxisLabel;

			if (stepSize !== null) {
				chart.options.scales.yAxes[0].ticks.stepSize = stepSize;
			} else {
				delete chart.options.scales.yAxes[0].ticks.stepSize;
			}
			if (suggestedMin !== null) {
				chart.options.scales.yAxes[0].ticks.suggestedMin = suggestedMin;
			} else {
				delete chart.options.scales.yAxes[0].ticks.suggestedMin;
			}
			if (suggestedMax !== null) {
				chart.options.scales.yAxes[0].ticks.suggestedMax = suggestedMax;
			} else {
				delete chart.options.scales.yAxes[0].ticks.suggestedMax;
			}
			titleDisplayString = propertyName;

			// TODO: downsample dataset?
			chart.data.datasets = [];

			let createDataset = (label, color, xaxisid = "x", yaxisid = "y") => {
				return {
					label: label,
					borderColor: color,
					backgroundColor: color,
					borderWidth: 0.75,
					rotation: 0,
					lineTension: 0,
					steppedLine: false,
					fill: false,
					xAxisID: "x",
					yAxisID: "y",
					data: []
				}
			}

			const colorChoicesOrder = ["blue", "red", "lime", "pink", "cyan", "orange"];
			for(let i = 0; i < doProperties.length; i++) {
				chart.data.datasets.push(createDataset("", 			colorChoicesOrder[i]));
			}
			for(let i = 0; i < doProperties.length; i++) {
				chart.data.datasets.push(createDataset(propertyName, "grey"));
			}

			chart.update();
		}
	}
	let updateChartData = () => {

		dataPointsBeforeTime = {};
		dataPoints = {};
		for(let j = 0; j < doProperties.length; j++) {
			dataPointsBeforeTime[doProperties[j]] = [];
			dataPoints[doProperties[j]] = [];
		}

		for(let i = 0; i < $data.hours.length; i++) {
			const time = moment.utc($data.hours[i].time);
			const sliderTime = moment(get(timeSlider.value));

			for(let j = 0; j < doProperties.length; j++) {
				const val = (doProperties.length > 1)
					? propertyOptions[$inPropertyIndex][j].getProperty($data, i)
					: propertyOptions[$inPropertyIndex].getProperty($data, i);
				const point = {
					x: time.toISOString(),
					y: val
				};
				dataPoints[doProperties[j]].push(point);
				if (time <= sliderTime) {
					dataPointsBeforeTime[doProperties[j]].push(point);
				}
			}
		}

		// console.log("dataPoints", dataPoints);
		// console.log("dataPointsBeforeTime", dataPointsBeforeTime);
		if (chart !== null) {
			let index = 0;
			for(let i = 0; i < doProperties.length; i++) {
				chart.data.datasets[index].data = dataPointsBeforeTime[doProperties[i]];
				index++;
			}
			for(let i = 0; i < doProperties.length; i++) {
				chart.data.datasets[index].data = dataPoints[doProperties[i]];
				index++;
			}
			chart.update();
		}
	};

	$: {
		$timeSliderValue;
		$timeSliderValueMax;
		if (canvas !== null) {
			updateChartData();
		}
	}

	let updateChart = ()=> {

		if (canvas !== null) {

			let config = {
				type: 'line',
				data: {
					datasets: []
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					title: {
						display: false
					},
					legend: {
						display: false,
						onClick: () => {}
					},
					tooltips: {
						enabled: true,
						filter: (item) => {
							if (item.datasetIndex < doProperties.length) { return false; }
							return true;
						},
						callbacks: {
							label: (item, data) => {
								let useIndex = item.datasetIndex % doProperties.length;
								const key = doProperties[useIndex];
								// console.log("useIndex", useIndex, "key", key);
								const graphProperty = findGraphPropertyByKey(key);
								return graphProperty.name + ": " + graphProperty.unitType.prefix + item.value + graphProperty.unitType.suffix;
							},
							footer: () => {
								if (User.hasFeature(Feature.TIME_SLIDER.VIEW)) {
									return "Click to change timeline.";
								}
								return "";
							}
						}
					},
					onClick: (event, elements) => {
						if (!User.hasFeature(Feature.TIME_SLIDER.VIEW)) {
							return;
						}
						if (elements.length > 0) {
							let index = elements[0]._index;
							let datasetIndex = elements[0]._datasetIndex;

							const timestamp = moment($data.hours[index].time);
							timeSlider.value.set(timestamp);
						}
					},
					scales: {
						xAxes: [
							{
								id: "x",
								type: "time",
								distribution: "linear",
								ticks: {
									source: 'auto'
								},
								scaleLabel: {
									display: true,
									position: "bottom",
									labelString: "Time"
								},
								time: {
									minUnit: "day",
									tooltipFormat: "MMMM Do YYYY, hh:mm:ssa [(UTC)]"
									// BUG: Using these crashes the browser with current  chart.js/moment versions.
									// unit: 'hourly',
									// displayFormats: {
									//  	hourly: "MMM D - HH:mm"
									// }
								},
								afterBuildTicks: function(scale, ticks) {
									return ticks;
								}
							}
						],
						yAxes: [
							{
								id: "y",
								scaleLabel: {
									display: true,
									position: "left",
									labelString: yAxisLabel
								},
								ticks: {
									//beginAtZero: false,
									stepSize: 1
								}
							}
						]
					}
				}
			};

			chart = new Chart(canvas.getContext("2d"), config);
			chart.update();
			updateChartAxes();
			updateChartData();
		}

	};

</script>

{#if propertyOptions.length > 0}
	<select bind:value={$inPropertyIndex} class="default" style="margin-bottom:8px; margin-top:8px; font-weight:bold; text-align-last:center; text-align:center;">
		{#each propertyOptions as option, index}
			{#if Array.isArray(option)}
				<option value={index}>{joinNames(option)}</option>
			{:else}
				<option value={index}>{option.name}</option>
			{/if}
		{/each}
	</select>
{:else}
	<p style="text-align:center;"><b>{ titleDisplayString }</b></p>
{/if}
<div class="infocard-chart-container" style={"position:relative; width:100%; min-height:" + height + "px; height:" + height + "px;"}>
	<canvas bind:this={canvas}></canvas>
</div>
